
import React from 'react';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Award, Users, Heart, Lightbulb, ArrowRight, TrendingUp } from 'lucide-react';
import { Progress } from '@/components/ui/progress';

interface FundingOverviewProps {
  onTabChange: (tab: string) => void;
}

const FundingOverview: React.FC<FundingOverviewProps> = ({ onTabChange }) => {
  return (
    <div className="p-6 space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="border shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle>Funding Progress</CardTitle>
            <CardDescription>Your active funding campaigns</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm font-medium">New Album</span>
                  <span className="text-sm font-medium">$3,245 / $8,500</span>
                </div>
                <Progress value={38} className="h-2" />
                <div className="flex justify-between mt-1">
                  <span className="text-xs text-muted-foreground">38% Complete</span>
                  <span className="text-xs text-muted-foreground">45 days left</span>
                </div>
              </div>
              
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm font-medium">Summer Tour</span>
                  <span className="text-sm font-medium">$1,875 / $5,000</span>
                </div>
                <Progress value={37.5} className="h-2" />
                <div className="flex justify-between mt-1">
                  <span className="text-xs text-muted-foreground">37.5% Complete</span>
                  <span className="text-xs text-muted-foreground">30 days left</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle>Funding Analytics</CardTitle>
            <CardDescription>Key metrics for your funding</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-lg bg-muted p-3">
                <div className="text-sm font-medium text-muted-foreground">Monthly Support</div>
                <div className="text-2xl font-bold">$487</div>
                <div className="text-xs text-green-500 flex items-center">
                  <TrendingUp className="h-3 w-3 mr-1" /> +12% from last month
                </div>
              </div>
              
              <div className="rounded-lg bg-muted p-3">
                <div className="text-sm font-medium text-muted-foreground">Total Supporters</div>
                <div className="text-2xl font-bold">57</div>
                <div className="text-xs text-green-500 flex items-center">
                  <TrendingUp className="h-3 w-3 mr-1" /> +5 new this month
                </div>
              </div>
              
              <div className="rounded-lg bg-muted p-3">
                <div className="text-sm font-medium text-muted-foreground">Avg. Contribution</div>
                <div className="text-2xl font-bold">$35</div>
                <div className="text-xs text-muted-foreground">Per supporter</div>
              </div>
              
              <div className="rounded-lg bg-muted p-3">
                <div className="text-sm font-medium text-muted-foreground">Retention Rate</div>
                <div className="text-2xl font-bold">92%</div>
                <div className="text-xs text-muted-foreground">Monthly supporters</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => onTabChange('grants')}>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="bg-purple-100 p-3 rounded-full">
                <Award className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <h3 className="font-medium">AI Grant Matchmaker</h3>
                <p className="text-sm text-muted-foreground mt-1">Find perfect grants for your career</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" className="w-full mt-4 justify-between">
              Explore Grants <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
        
        <Card className="border shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => onTabChange('collaborative')}>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="bg-blue-100 p-3 rounded-full">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-medium">Collaborative Funding</h3>
                <p className="text-sm text-muted-foreground mt-1">Pool resources with other artists</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" className="w-full mt-4 justify-between">
              Find Collaborators <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
        
        <Card className="border shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => onTabChange('support')}>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="bg-red-100 p-3 rounded-full">
                <Heart className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <h3 className="font-medium">Direct Support Hub</h3>
                <p className="text-sm text-muted-foreground mt-1">Optimize your supporter tiers</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" className="w-full mt-4 justify-between">
              Manage Support <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
        
        <Card className="border shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => onTabChange('strategist')}>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="bg-amber-100 p-3 rounded-full">
                <Lightbulb className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <h3 className="font-medium">AI Funding Strategist</h3>
                <p className="text-sm text-muted-foreground mt-1">Get personalized funding roadmap</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" className="w-full mt-4 justify-between">
              Get Strategy <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      </div>

      <Card className="border shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle>Recent Funding Activity</CardTitle>
          <CardDescription>Latest updates from your funding campaigns</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-start gap-3 pb-3 border-b">
              <div className="w-9 h-9 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                <Heart className="h-4 w-4 text-green-600" />
              </div>
              <div>
                <h4 className="font-medium">New supporter joined your monthly tier</h4>
                <p className="text-sm text-muted-foreground mt-1">Sarah K. is now supporting you with $15/month</p>
                <p className="text-xs text-muted-foreground mt-1">2 hours ago</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 pb-3 border-b">
              <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <Award className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <h4 className="font-medium">New grant match found</h4>
                <p className="text-sm text-muted-foreground mt-1">You have a 85% match with "Emerging Artists Fund"</p>
                <p className="text-xs text-muted-foreground mt-1">Yesterday</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                <Users className="h-4 w-4 text-purple-600" />
              </div>
              <div>
                <h4 className="font-medium">Collaborative funding pool reached milestone</h4>
                <p className="text-sm text-muted-foreground mt-1">Tour fund with 3 other artists reached 50% of goal</p>
                <p className="text-xs text-muted-foreground mt-1">2 days ago</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FundingOverview;
